/* eslint-disable react-perf/jsx-no-new-function-as-prop */
import React from 'react';
import { connect } from 'react-redux';

import PlusIcon from 'mdi-react/PlusIcon';
import PencilIcon from 'mdi-react/PencilIcon';

import { AppContext } from '@prisjakt/ui-app-context';

import { layout } from '@prisjakt/ui-base';
import ListDnd from '../../components/list-dnd/list-dnd';
import ListItem, { ListItemProps } from '../../components/item-list/list-item';
import EmptyList from '../../components/empty-list';
import DataContainer from '../../../../components/data';
import Loader from '../../../../components/loader';
import SidebarHeaderDesktop from '../../components/sidebar-desktop/sidebar-header-desktop';
import SidebarHeaderMobile from '../../components/sidebar-mobile/sidebar-header-mobile';
import SettingsDialog from '../../components/settings-dialog';
import ItemList from '../../components/item-list';
import ConfirmRearrange from '../../components/confirm-rearrange';

import actions from '../../actions/lists';
import listsQuery from '../../lists-query.graphql';
import { SORT_OPTIONS } from '../../constants';
import { VIEWS } from '../../components/sidebar-mobile/constants';
import { AppState } from '../../../../reducers';

import { trackSidebarDragAndDrop } from '../../tracking';

import * as S from './styles';
import * as SharedStyles from '../styles';

import { SidebarListsQuery } from '../../../../types';
import {
  Translate,
  Draggable,
  ToggleDraggable,
} from '../../components/sharedTypes';
import { View, DeletedLists, SelectList } from '../../types';
import UnauthenticatedLists from '../../components/item-list/unauthenticated-lists';

type OwnProps = {
  deletedLists?: DeletedLists;
  listRefresh: number;
  onOpenView?: (view: View) => void;
  isAuthenticated?: boolean;
  onLinkClick?: () => void;
  draggable: Draggable;
  toggleDraggable?: ToggleDraggable;
  isMobile?: boolean;
};

type DispatchProps = {
  removeListRequest: (id: number) => void;
  orderListsRequest: (id: number) => void;
  selectList: SelectList;
  createListSuccess: (list: string) => void;
};

type DataProps = {
  data: SidebarListsQuery;
  mutation: UpdateNameMutation;
  fetchMore: () => void;
  fetching: boolean;
};

type UpdateNameMutation = (
  query: string,
  variables?: {}
) => { data: { listCreate: { list: string } } };

type Props = OwnProps & DispatchProps;
type State = {
  showSettings: boolean;
};

class Lists extends React.PureComponent<Props, State> {
  state = {
    showSettings: false,
  };

  static contextType = AppContext;

  openSettings = () => {
    this.setState({ showSettings: true });
  };

  closeSettings = () => {
    this.setState({ showSettings: false });
  };

  renderLists = (
    props: Props,
    t: Translate,
    lists: DataProps['data']['me']['lists'],
    fetchMore: DataProps['fetchMore'],
    fetching: DataProps['fetching']
  ) => {
    if (!lists) return null;

    const listsProps = {
      pageInfo: lists.pageInfo,
      nodes: lists.nodes,
      fetching,
      onFetchMore: () => {
        fetchMore();
      },
      renderEmpty: (
        <EmptyList
          text={t('lists', 'list.empty-helper-text')}
          icon={<SharedStyles.CreateListIcon size={64} />}
        />
      ),
      deleted: props.deletedLists,
      onClick: (id: number) => {
        props.selectList(id);
        if (props.onOpenView) {
          props.onOpenView(VIEWS.LIST);
        }
      },
      onDelete: (id: number) => {
        props.removeListRequest(id);
      },
      onOrder: (listIds: number) => {
        trackSidebarDragAndDrop('lists');
        props.orderListsRequest(listIds);
      },
    };

    return lists &&
      lists.nodes &&
      lists.nodes.length > 1 &&
      this.props.draggable ? (
      <ListDnd
        hasDragHandle={Boolean(this.props.toggleDraggable)}
        render={(itemProps: ListItemProps) => <ListItem {...itemProps} />}
        {...listsProps}
      />
    ) : (
      <ItemList {...listsProps} />
    );
  };

  async updateListName(name: string, mutation: UpdateNameMutation) {
    const { data } = await mutation(
      `mutation listCreate($name: String!) {
        listCreate(name: $name) {
          success
          list {
            id
            name
          }
        }
      }`,
      {
        name,
      }
    );

    this.props.createListSuccess(data.listCreate.list);
    this.closeSettings();
  }

  openRootView = () => {
    return this.props.onOpenView && this.props.onOpenView(VIEWS.ROOT);
  };

  render() {
    const { t } = this.context;
    const {
      listRefresh,
      isMobile,
      isAuthenticated,
      onLinkClick,
      onOpenView,
    } = this.props;

    if (!isAuthenticated) {
      return (
        <UnauthenticatedLists
          isMobile={isMobile}
          onLoginClick={onLinkClick}
          onHeaderClick={this.openRootView}
        />
      );
    }

    return (
      <DataContainer
        refreshKey={listRefresh}
        query={listsQuery}
        renderLoader={() => <Loader fadeIn />}
        variables={{
          limit: 30,
          sort: SORT_OPTIONS.USER_DEFINED,
        }}
        // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
        render={(dataProps: DataProps) => {
          const { data, mutation, fetchMore, fetching } = dataProps;

          if (!data || !data.me || !data.me.lists) {
            return null;
          }

          if (this.state.showSettings) {
            return (
              <>
                <SharedStyles.View>
                  <SettingsDialog
                    placeholder={t('product', 'list.new-list-suggested-name')}
                    showCancel
                    onConfirm={
                      mutation
                        ? (name: string) => {
                            this.updateListName(name, mutation);
                          }
                        : null
                    }
                    onCancel={this.closeSettings}
                    confirmLabel={t('common', 'button.save')}
                  />
                </SharedStyles.View>
              </>
            );
          }

          const { lists } = data.me;
          return (
            <>
              {isMobile ? (
                <SidebarHeaderMobile
                  useDropdown
                  title={t('lists', 'lists.title')}
                  onClickHandler={onOpenView && (() => onOpenView(VIEWS.ROOT))}
                  options={
                    this.props.toggleDraggable && lists.nodes.length > 0
                      ? [
                          {
                            title: t('lists', 'list.change-order'),
                            handler: this.props.toggleDraggable,
                          },
                        ]
                      : undefined
                  }
                  settingsIcon={<PencilIcon />}
                />
              ) : (
                <SidebarHeaderDesktop title={t('lists', 'lists.title')} />
              )}

              {this.props.draggable && this.props.toggleDraggable && (
                <ConfirmRearrange onClick={this.props.toggleDraggable} />
              )}

              {lists && (
                <SharedStyles.View style={{ padding: 0 }}>
                  {this.renderLists(this.props, t, lists, fetchMore, fetching)}
                </SharedStyles.View>
              )}

              <SharedStyles.View
                style={{
                  position: 'sticky',
                  bottom: 0,
                  marginTop: layout.spacing.xSmall,
                }}
              >
                <S.CreateListButton
                  color="primary"
                  variant="outlined"
                  icon={<PlusIcon size={16} />}
                  iconPosition="left"
                  style={{ width: '100%' }}
                  onClick={this.openSettings}
                >
                  {t('product', 'list.create-new-list')}
                </S.CreateListButton>
              </SharedStyles.View>
            </>
          );
        }}
      />
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  deletedLists: state.areas.sidebar.deletedLists,
  listRefresh: state.areas.sidebar.listRefresh,
  isAuthenticated: state.areas.account.isAuthenticated,
});

const dispatchActions = {
  removeListRequest: actions.removeListRequest,
  orderListsRequest: actions.orderListsRequest,
  selectList: actions.selectList,
  createListSuccess: actions.createListSuccess,
};

export default connect(mapStateToProps, dispatchActions)(Lists);
